import React, { memo, useState } from "react";
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TableSortLabel,
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Collapse,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";

export interface RDTColumn {
  name: string | React.ReactNode;
  selector?: (row: any) => React.ReactNode;
  width?: string;
  sortable?: boolean;
  onCellClick?: (row: any) => void;
  customRender?: (row: any, rowIndex?: number) => React.ReactNode;
  tooltip?: (row: any) => string;
  align?: "left" | "center" | "right";
  sortKey?: string;
}

interface TableProps {
  columns: RDTColumn[];
  data: any[];
  className?: string;
  isTableBodyScroll?: boolean;
  maxHeight?: string;
  paginationProps?: {
    isPagination: boolean;
    totalCount: number;
    pageSelected: number;
    setPageSelected: (page: number) => void;
    rowsPerPageValue: number;
    setRowsPerPageValue: (rows: number) => void;
  };
  noDataText?: string;
  showCheckbox?: boolean;
  checkboxWidth?: string;
  onSelectionChange?: (ids: any[], isSelectAll?: boolean) => void;
  selectedRows?: any[];
  showExpandedRow?: boolean;
  loading?: boolean;
  onSortChange?: (sortBy: string) => void;
  tableWidth?: string;
  expandedRowRender?: (row: any, rowIndex: number, columns: RDTColumn[]) => React.ReactNode;
  isRowExpandable?: (row: any) => boolean;
  rowKey?: string;
  getRowStyle?: (row: any) => React.CSSProperties;
  showRegularizedRow?: boolean;
  regularizedRowRender?: (row: any, rowIndex: number) => React.ReactNode;
  isGrid?: boolean;
  rowHeight?: number;
  isTileHeader?: boolean;
  sortState?: Record<string, "asc" | "desc">;
  setSortState?: React.Dispatch<React.SetStateAction<Record<string, "asc" | "desc">>>;
  truncateCellContent?: boolean;
  /** Number of visible lines for normal string cells before ellipsis. */
  truncateLines?: number;
  checkboxCondition?: (row: any) => boolean;
  /** Body cell vertical alignment. Use "top" only for stacked custom content (e.g. approver chips). */
  bodyVerticalAlign?: "middle" | "top" | "bottom";
  selectAllCheckbox?: boolean; // select all checkbox across pages
  forceSelectAll?: boolean;
  paginationLabel?: string;
}

const CHECKBOX_COL_WIDTH = 30;

const HEADER_BG = "#f4f8fc";
const HEADER_BORDER = "1px solid #dce6f0";
const ROW_BORDER = "1px solid #e4ebf3";
const TABLE_BORDER = "#dce6f0";
const HEADER_TEXT = "#60738f";
const BODY_TEXT = "#26364d";
const LINK_TEXT = "#2563eb";
const MUTED_TEXT = "#6f829e";

const TABLE_FONT_SIZE = "1rem";
const TABLE_CELL_PADDING = "0.725rem 1rem";
const TABLE_HEADER_CELL_PADDING = "1rem";
const TABLE_LINE_HEIGHT = 1.45;
const GRID_HEADER_HEIGHT = 56;

const checkboxCellSx = {
  width: CHECKBOX_COL_WIDTH,
  minWidth: CHECKBOX_COL_WIDTH,
  maxWidth: CHECKBOX_COL_WIDTH,
  padding: "0.625rem 0.5rem",
  verticalAlign: "middle" as const,
  boxSizing: "border-box" as const,
  borderBottom: ROW_BORDER,
};

const stickyHeaderSx = {
  position: "sticky" as const,
  top: 0,
  backgroundColor: HEADER_BG,
  borderBottom: HEADER_BORDER,
  zIndex: 2,
};

const gridHeaderRowSx = {
  height: GRID_HEADER_HEIGHT,
};

const gridHeaderCellSx = {
  minHeight: GRID_HEADER_HEIGHT,
  padding: TABLE_HEADER_CELL_PADDING,
  boxSizing: "border-box" as const,
  verticalAlign: "middle" as const,
  overflow: "visible",
  whiteSpace: "normal" as const,
  wordBreak: "normal" as const,
};

const gridTileHeaderBoxSx = {
  width: "100%",
  height: "100%",
  maxHeight: "100%",
  padding: "0.25rem 0.375rem",
  backgroundColor: "#f0f0f0",
  borderRadius: "0.375rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
  border: "1px solid #ddd",
  fontWeight: 600,
  fontSize: TABLE_FONT_SIZE,
  boxSizing: "border-box" as const,
  overflow: "hidden",
};

const sortLabelSx = {
  width: "100%",
  justifyContent: "flex-start",
  fontSize: TABLE_FONT_SIZE,
  fontWeight: 600,
  lineHeight: TABLE_LINE_HEIGHT,
  color: "inherit",
  whiteSpace: "normal",
  overflow: "visible",
  textOverflow: "clip",
  wordBreak: "normal",

  "&.Mui-active": {
    color: "inherit"
  },

  "& .MuiTableSortLabel-icon": {
    opacity: 0.55,
    fontSize: "1.125rem",
    marginLeft: "0.4375rem",
    color: "#8da0b8",
  },

  "&:hover .MuiTableSortLabel-icon": {
    opacity: 0.9
  },

  "&.Mui-active .MuiTableSortLabel-icon": {
    opacity: 1,
    color: "#7086a3",
  },
};

const CellText: React.FC<{
  value: string;
  lines?: number;
  pointer?: boolean;
}> = ({ value, lines = 2, pointer = false }) => {
  const textRef = React.useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  React.useEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const checkTruncation = () => {
      setIsTruncated(
        element.scrollHeight > element.clientHeight + 1 ||
        element.scrollWidth > element.clientWidth + 1
      );
    };

    checkTruncation();

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(element);

    return () => observer.disconnect();
  }, [value, lines]);

  return (
    <Box
      ref={textRef}
      component="span"
      title={isTruncated ? value : undefined}
      sx={{
        display: "-webkit-box",
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: lines,
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        wordBreak: "normal",
        lineHeight: TABLE_LINE_HEIGHT,
        fontSize: TABLE_FONT_SIZE,
        color: "inherit",
        cursor: pointer ? "pointer" : "inherit",
      }}
    >
      {value}
    </Box>
  );
};

const PaginationButton = ({
  active = false,
  disabled = false,
  children,
  onClick,
  ariaLabel,
}: {
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  ariaLabel?: string;
}) => (
  <Box
    component="button"
    type="button"
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={onClick}
    sx={{
      width: "2rem",
      height: "2rem",
      minWidth: "2rem",
      padding: 0,
      border: "1px solid #d9e2ec",
      borderRadius: "0.375rem",
      backgroundColor: active ? "#2563eb" : "#fff",
      color: active ? "#fff" : "#475569",
      fontSize: TABLE_FONT_SIZE,
      lineHeight: TABLE_LINE_HEIGHT,
      fontFamily: "inherit",
      fontWeight: active ? 500 : 400,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: disabled ? "default" : "pointer",
      boxSizing: "border-box",
      transition: "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",

      "&:hover": {
        backgroundColor: disabled ? "#fff" : active ? "#2563eb" : "#f1f5f9",
        borderColor: disabled ? "#d9e2ec" : active ? "#2563eb" : "#cbd5e1",
        color: disabled ? "#cbd5e1" : active ? "#fff" : "#334155",
      },

      "&:disabled": {
        color: "#cbd5e1",
        borderColor: "#e2e8f0",
        backgroundColor: "#fff",
      },
    }}
  >
    {children}
  </Box>
);

const Table: React.FC<TableProps> = ({
  columns,
  data,
  className,
  isTableBodyScroll = true,
  maxHeight,
  paginationProps,
  noDataText = "No Records Found",
  showCheckbox = false,
  checkboxWidth = "30px",
  onSelectionChange,
  selectedRows,
  showExpandedRow,
  loading = false,
  onSortChange,
  tableWidth,
  expandedRowRender,
  isRowExpandable = () => true,
  rowKey = "Id",
  getRowStyle,
  showRegularizedRow,
  regularizedRowRender,
  isGrid = false,
  rowHeight = 56,
  isTileHeader = false,
  sortState = {},
  setSortState = () => { },
  truncateCellContent = true,
  truncateLines = 2,
  checkboxCondition,
  bodyVerticalAlign = "middle",
  selectAllCheckbox = false,
  forceSelectAll = false,
  paginationLabel = "records"
}) => {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [expandedRowId, setExpandedRowId] = useState<any>(null);

  const selected = selectedRows || [];

  const getRowId = (row: any, index?: number) => row?.[rowKey] ?? `row-${index}`;

  const selectableRows = checkboxCondition
    ? data.filter((row) => checkboxCondition(row))
    : data;

  const toggleSelectAll = (checked: boolean) => {
    if (selectAllCheckbox) {
      if (forceSelectAll && checked === false) {
        onSelectionChange?.([], false);
        return;
      }

      onSelectionChange?.([], checked);
      return;
    }

    const ids = checked ? selectableRows.map((r, i) => getRowId(r, i)) : [];
    onSelectionChange?.(ids, checked);
  };

  const toggleRow = (id: any) => {
    const newSelected = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id];

    onSelectionChange?.(newSelected);
  };

  const isAllSelected = forceSelectAll
    ? true
    : (
      selectableRows.length > 0 &&
      selectableRows.every((row, index) =>
        selected.includes(getRowId(row, index))
      )
    );

  const isPartial = !forceSelectAll &&
    selected.length > 0 &&
    selected.length < selectableRows.length;

  const toggleExpand = (row: any, index: number) => {
    if (!isRowExpandable?.(row)) return;

    const id = getRowId(row, index);

    setExpandedRowId((prev: any) =>
      prev === id ? null : id
    );
  };

  const handleSort = (col: RDTColumn) => {
    if (!col.sortKey) return;

    const key = col.sortKey;

    const next = sortState[key] === "asc" ? "desc" : "asc";

    const updated: Record<string, "asc" | "desc"> = {
      ...sortState,
      [key]: next,
    };

    setSortState?.(updated);
    onSortChange?.(`${key}${next.toUpperCase()}`);
  };

  const extraColumns = (showCheckbox ? 1 : 0) + (showExpandedRow ? 1 : 0);
  const hasPagination = paginationProps?.isPagination;

  const totalPages = paginationProps ? Math.max(1, Math.ceil(paginationProps.totalCount / paginationProps.rowsPerPageValue)) : 1;
  const currentPage = paginationProps?.pageSelected || 1;


  const getPaginationItems = () => {
    const pages: (| number | "ellipsis")[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }

      return pages;
    }

    if (currentPage <= 4) {
      return [
        1,
        2,
        3,
        4,
        5,
        "ellipsis",
        totalPages,
      ];
    }

    if (currentPage >= totalPages - 3) {
      return [
        1,
        "ellipsis",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    return [
      1,
      "ellipsis",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "ellipsis",
      totalPages,
    ];
  };

  const paginationItems = getPaginationItems();

  const goToPage = (page: number) => {
    if (!paginationProps || page < 1 || page > totalPages) {
      return;
    }

    paginationProps.setPageSelected(page);
  };

  return (
    <Box
      sx={{
        width: "100%",
        border: isGrid ? "none" : `1px solid ${TABLE_BORDER}`,
        borderRadius: isGrid ? 0 : "0.4375rem",
        backgroundColor: "#fff",
        overflow: "hidden",
        boxShadow: "none",
        fontSize: TABLE_FONT_SIZE,
      }}
      className={className}
    >
      <TableContainer
        sx={{
          width: "100%",
          maxHeight: isGrid ? "unset" : isTableBodyScroll ? (maxHeight || "65vh") : "unset",
          overflowY: isGrid ? "visible" : isTableBodyScroll ? "auto" : "visible",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          border: 0,
          borderRadius: 0,
          backgroundColor: "#fff",
        }}
      >
        <MuiTable
          stickyHeader
          sx={{
            tableLayout: isMobile ? "auto" : "fixed",
            minWidth: isMobile ? "max-content" : 0,
            width: tableWidth || "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
            backgroundColor: "#fff",
            fontSize: TABLE_FONT_SIZE,
            "& .MuiTableCell-root": {
              fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
              fontSize: TABLE_FONT_SIZE,
            },
          }}
        >
          {/* Table Head */}
          <TableHead>
            <TableRow sx={isGrid ? gridHeaderRowSx : undefined}>
              {showCheckbox && (
                <TableCell
                  padding="checkbox"
                  sx={{
                    ...checkboxCellSx,
                    ...stickyHeaderSx,
                    ...(isGrid ? gridHeaderCellSx : {}),
                    zIndex: 3,
                    width: checkboxWidth,
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={isPartial}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    sx={{
                      p: "0.25rem",
                    }}
                  />
                </TableCell>
              )}
              {columns.map((col, index) => (
                <TableCell
                  key={index}
                  align={col.align || "left"}
                  sx={{
                    width: col.width || "auto",
                    color: HEADER_TEXT,
                    fontSize: TABLE_FONT_SIZE,
                    fontWeight: 600,
                    lineHeight: TABLE_LINE_HEIGHT,
                    letterSpacing: "0.01em",
                    whiteSpace: "normal",
                    overflow: "visible",
                    textOverflow: "clip",
                    wordBreak: "normal",
                    ...stickyHeaderSx,
                    ...(isGrid ? gridHeaderCellSx : {}),
                    zIndex: 2,
                    padding: TABLE_HEADER_CELL_PADDING,
                    ...(col.sortable && col.sortKey
                      ? {
                        cursor: "pointer",
                        "&:hover .MuiTableSortLabel-icon": { opacity: 0.6 },
                      }
                      : {}),
                  }}
                >
                  {isTileHeader ? (
                    <Box
                      sx={
                        isGrid
                          ? gridTileHeaderBoxSx
                          : {
                            width: "100%",
                            padding: "0.375rem 0.625rem",
                            backgroundColor: "#f0f0f0",
                            borderRadius: "0.375rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                            border: "1px solid #ddd",
                            fontWeight: 600,
                            fontSize: TABLE_FONT_SIZE,
                            boxSizing: "border-box",
                          }
                      }
                    >
                      {col.name}
                    </Box>
                  ) : col.sortable && col.sortKey ? (
                    <TableSortLabel
                      active={!!sortState[col.sortKey]}
                      direction={sortState[col.sortKey] || "asc"}
                      onClick={() => handleSort(col)}
                      sx={sortLabelSx}
                    >
                      {col.name}
                    </TableSortLabel>
                  ) : (
                    col.name
                  )}
                </TableCell>
              ))}

              {showExpandedRow && (
                <TableCell
                  sx={{
                    width: "3.125rem",
                    ...stickyHeaderSx,
                    zIndex: 3,
                    padding: TABLE_CELL_PADDING,
                  }}
                />
              )}
            </TableRow>
          </TableHead>

          {/* Table Body */}
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + extraColumns}
                  align="center"
                  sx={{
                    padding: TABLE_CELL_PADDING,
                    fontSize: TABLE_FONT_SIZE,
                  }}
                >
                  <CircularProgress
                    size="1.75em"
                  />
                </TableCell>
              </TableRow>
            ) : data?.length > 0 ? (
              data.map((row, rowIndex) => {
                const rowId = getRowId(row, rowIndex);

                return (
                  <React.Fragment key={rowId}>
                    <TableRow
                      hover={false}
                      sx={{
                        height: isGrid ? rowHeight : "auto",
                        ...(getRowStyle ? getRowStyle(row) : {}),
                        "&:last-child td": {
                          borderBottom: "none",
                        },
                      }}
                    >
                      {showCheckbox && (
                        <TableCell
                          padding="checkbox"
                          sx={{
                            ...checkboxCellSx,
                            fontSize: TABLE_FONT_SIZE,
                          }}
                        >
                          {checkboxCondition ? (
                            checkboxCondition(row) && (
                              <Checkbox
                                size="small"
                                checked={forceSelectAll ? !selected.includes(rowId) : selected.includes(rowId)}
                                onChange={() => toggleRow(rowId)}
                                sx={{
                                  p: "0.25rem",
                                }}
                              />
                            )
                          ) : (
                            <Checkbox
                              size="small"
                              checked={forceSelectAll ? !selected.includes(rowId) : selected.includes(rowId)}
                              onChange={() => toggleRow(rowId)}
                              sx={{
                                p: "0.25rem",
                              }}
                            />
                          )}
                        </TableCell>
                      )}

                      {columns.map((col, colIndex) => {
                        const content =
                          col.customRender?.(row, rowIndex) ??
                          col.selector?.(row) ??
                          "-";

                        const isCustomCell = typeof content !== "string";
                        const isClickable = Boolean(col.onCellClick);

                        return (
                          <TableCell
                            key={colIndex}
                            align={col.align || "left"}
                            sx={{
                              cursor: isClickable ? "pointer" : "default",
                              color: isClickable ? LINK_TEXT : BODY_TEXT,
                              fontSize: TABLE_FONT_SIZE,
                              fontWeight: 400,
                              lineHeight: TABLE_LINE_HEIGHT,
                              width: col.width || "auto",
                              minWidth: 0,
                              maxWidth: col.width || "none",
                              verticalAlign: bodyVerticalAlign,
                              overflow: isCustomCell ? "visible" : "hidden",
                              borderBottom:
                                row.IsModifiedByAdmin && showRegularizedRow
                                  ? "none"
                                  : ROW_BORDER,
                              padding: TABLE_CELL_PADDING,
                              boxSizing: "border-box",
                              overflowWrap: "anywhere",
                              "& > *": {
                                minWidth: 0,
                                maxWidth: "100%",
                              },
                            }}
                            onClick={() => col.onCellClick?.(row)}
                          >
                            {typeof content === "string" && truncateCellContent ? (
                              <CellText
                                value={content}
                                lines={truncateLines}
                                pointer={isClickable}
                              />
                            ) : (
                              content
                            )}
                          </TableCell>
                        );
                      })}

                      {/* Arrow icon cell */}
                      {showExpandedRow && (
                        <TableCell
                          sx={{
                            width: "3.125rem",
                            padding: TABLE_CELL_PADDING,
                            fontSize: TABLE_FONT_SIZE,
                          }}
                        >
                          {isRowExpandable(row) && (
                            <IconButton
                              size="small"
                              onClick={() => toggleExpand(row, rowIndex)}
                              sx={{
                                p: "0.25rem",
                              }}
                            >
                              {expandedRowId === rowId ? (
                                <KeyboardArrowDownIcon
                                  sx={{
                                    fontSize:
                                      "1.25rem",
                                  }}
                                />
                              ) : (
                                <KeyboardArrowRightIcon
                                  sx={{
                                    fontSize:
                                      "1.25rem",
                                  }}
                                />
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                      )}
                    </TableRow>

                    {/* Expanded Row */}
                    {showExpandedRow && expandedRowId === rowId && (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length + extraColumns}
                          sx={{ p: 0, minWidth: "100%" }}
                        >
                          <Collapse in timeout="auto" unmountOnExit>
                            <Box sx={{ minWidth: "max-content" }}>
                              {expandedRowRender?.(row, rowIndex, columns)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* show regularized row */}
                    {showRegularizedRow && row.IsModifiedByAdmin && (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length + extraColumns}
                          sx={{
                            pt: 0,
                            minWidth: "100%",
                            color: "#09f620",
                            fontSize: TABLE_FONT_SIZE,
                          }}
                        >
                          {regularizedRowRender ? (
                            regularizedRowRender(row, rowIndex)
                          ) : (
                            `Regularized by ${row.ModifiedBy}`
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length + extraColumns}
                  align="center"
                  sx={{
                    padding: TABLE_CELL_PADDING,
                    fontSize: TABLE_FONT_SIZE,
                  }}
                >
                  <Typography
                    color="textSecondary"
                    sx={{
                      py: "1.5rem",
                      fontSize: TABLE_FONT_SIZE,
                      lineHeight: TABLE_LINE_HEIGHT,
                    }}
                  >
                    {noDataText}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>

      {/* Pagination */}
      {hasPagination && paginationProps && (
        <Box
          sx={{
            width: "100%",
            minHeight: "4rem",
            borderTop: "1px solid #e6eaf0",
            backgroundColor: "#fff",
            color: MUTED_TEXT,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
            padding: TABLE_CELL_PADDING,
            overflow: "hidden",
            fontSize: TABLE_FONT_SIZE,

            [theme.breakpoints.down("sm")]: {
              minHeight: "auto",
              flexWrap: "wrap",
              justifyContent: "center",
              padding: TABLE_CELL_PADDING,
              rowGap: "0.5rem",
            },
          }}
        >
          {/* LEFT SIDE - Showing count */}
          <Typography
            component="span"
            sx={{
              fontSize: TABLE_FONT_SIZE,
              lineHeight: TABLE_LINE_HEIGHT,
              color: MUTED_TEXT,
              margin: 0,
              whiteSpace: "nowrap",
              flexShrink: 0,

              [theme.breakpoints.down("sm")]: {
                width: "100%",
                textAlign: "center",
              },
            }}
          >
            Showing {Math.min(data.length, paginationProps.rowsPerPageValue)} of{" "}
            {paginationProps.totalCount} {paginationLabel}
          </Typography>

          {/* RIGHT SIDE */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "0.375rem",
              flexShrink: 0,

              [theme.breakpoints.down("sm")]: {
                justifyContent: "center",
                flexWrap: "wrap",
              },
            }}
          >
            {/* Items per page */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.375rem",
                flexShrink: 0,
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: TABLE_FONT_SIZE,
                  lineHeight: TABLE_LINE_HEIGHT,
                  color: MUTED_TEXT,
                  margin: 0,
                  whiteSpace: "nowrap",
                }}
              >
                Items per page:
              </Typography>

              <Select
                value={paginationProps.rowsPerPageValue}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  paginationProps.setRowsPerPageValue(value);
                  paginationProps.setPageSelected(1);
                }}
                size="small"
                variant="outlined"
                sx={{
                  minWidth: "3.5rem",
                  height: "2rem",
                  fontSize: TABLE_FONT_SIZE,
                  color: "#344054",
                  borderRadius: "0.375rem",
                  backgroundColor: "#fff",

                  "& .MuiSelect-select": {
                    fontSize: TABLE_FONT_SIZE,
                    lineHeight: TABLE_LINE_HEIGHT,
                    padding: "0.25rem 1.875rem 0.25rem 0.625rem",
                    display: "flex",
                    alignItems: "center",
                    minHeight: "auto",
                  },

                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #d9e2ec",
                  },

                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#c7d3e0",
                  },

                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #b8c7d8",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": {
                        fontSize: TABLE_FONT_SIZE,
                        minHeight: "2.25rem",
                      },
                    },
                  },
                }}
              >
                {[10, 30, 50, 100].map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                    sx={{
                      fontSize: TABLE_FONT_SIZE,
                    }}
                  >
                    {option}
                  </MenuItem>
                )
                )}
              </Select>
            </Box>

            {/* Previous */}
            <PaginationButton
              disabled={currentPage <= 1}
              ariaLabel="Previous page"
              onClick={() =>
                goToPage(currentPage - 1)
              }
            >
              <KeyboardArrowLeftIcon
                sx={{
                  fontSize: "1.25rem",
                }}
              />
            </PaginationButton>

            {/* Page numbers */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
                flexShrink: 0,
              }}
            >
              {paginationItems.map((item, index) => item === "ellipsis" ? (
                <Box
                  key={`ellipsis-${index}`}
                  sx={{
                    width: "2rem",
                    height: "2rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#64748b",
                    fontSize: TABLE_FONT_SIZE,
                    lineHeight: TABLE_LINE_HEIGHT,
                    userSelect: "none",
                  }}
                >
                  ...
                </Box>
              ) : (
                <PaginationButton
                  key={item}
                  active={item === currentPage}
                  onClick={() => goToPage(Number(item))}
                >
                  {item}
                </PaginationButton>
              )
              )}
            </Box>

            {/* Next */}
            <PaginationButton
              disabled={currentPage >= totalPages}
              ariaLabel="Next page"
              onClick={() => goToPage(currentPage + 1)}
            >
              <KeyboardArrowRightOutlinedIcon
                sx={{
                  fontSize: "1.25rem",
                }}
              />
            </PaginationButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default memo(Table);
